import { Link, type LinkProps } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Icone, type NomIcone } from './Icone'

// Briques d'interface partagées : en-tête de page, onglets segmentés, filtres, titres de carte.

type ActionPage =
  | { icone: NomIcone; libelle: string; onClick: () => void }
  | { icone: NomIcone; libelle: string; lien: LinkProps['to']; recherche?: LinkProps['search'] }

/** En-tête de page : surtitre, titre, et l'action principale de la page s'il y en a une. */
export function EntetePage({
  surtitre,
  titre,
  logo,
  action,
}: {
  surtitre: string
  titre: string
  logo?: ReactNode
  action?: ActionPage | undefined
}) {
  return (
    <header className="entete-page">
      <div className="entete-page-titre">
        <span className="surtitre">{surtitre}</span>
        <h1 className={logo ? 'titre-logo' : undefined}>
          {logo}
          {titre}
        </h1>
      </div>
      {action && (
        <div className="entete-page-actions">
          {'lien' in action ? (
            <Link to={action.lien} search={action.recherche} className="bouton bouton-principal">
              <Icone nom={action.icone} />
              {action.libelle}
            </Link>
          ) : (
            <button type="button" className="bouton bouton-principal" onClick={action.onClick}>
              <Icone nom={action.icone} />
              {action.libelle}
            </button>
          )}
        </div>
      )}
    </header>
  )
}

/** Onglets segmentés : icône et compteur facultatifs. */
export function Onglets<V extends string>({
  valeur,
  onChange,
  onglets,
  defilants = false,
  soulignes = false,
}: {
  valeur: V
  onChange: (v: V) => void
  onglets: { valeur: V; libelle: string; icone?: NomIcone; compte?: number | undefined }[]
  defilants?: boolean
  /** Second niveau d'onglets, sous un onglet principal : simple soulignement. */
  soulignes?: boolean
}) {
  const classe = soulignes
    ? 'onglets-soulignes'
    : defilants
      ? 'onglets onglets-defilants'
      : 'onglets'
  return (
    <div className={classe} role="tablist">
      {onglets.map((o) => (
        <button
          key={o.valeur}
          type="button"
          role="tab"
          aria-selected={valeur === o.valeur}
          className={soulignes ? undefined : valeur === o.valeur ? 'onglet actif' : 'onglet'}
          onClick={() => onChange(o.valeur)}
        >
          {o.icone && <Icone nom={o.icone} />}
          {o.libelle}
          {o.compte !== undefined && <span className="mono">{o.compte}</span>}
        </button>
      ))}
    </div>
  )
}

/** Liste déroulante habillée en pastille, avec une icône à gauche. */
export function Filtre({
  icone,
  libelle,
  id,
  valeur,
  onChange,
  options,
}: {
  icone: NomIcone
  libelle: string
  id?: string
  valeur: string
  onChange: (v: string) => void
  options: { valeur: string; libelle: string }[]
}) {
  return (
    <label className="filtre">
      <Icone nom={icone} />
      <select
        id={id}
        aria-label={libelle}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((o) => (
          <option key={o.valeur} value={o.valeur}>
            {o.libelle}
          </option>
        ))}
      </select>
      <Icone nom="keyboard_arrow_down" className="chevron" />
    </label>
  )
}

/** Titre de carte : icône, titre, et un lien à droite. */
export function TitreCarte({
  icone,
  titre,
  suffixe,
  lien,
}: {
  icone: NomIcone
  titre: string
  suffixe?: ReactNode
  lien?: { to: LinkProps['to']; libelle: string; fleche?: boolean } | undefined
}) {
  return (
    <div className="section-titre">
      <h2>
        <Icone nom={icone} />
        {titre}
        {suffixe}
      </h2>
      {lien && (
        <Link to={lien.to} className="lien-fleche">
          {lien.libelle}
          {lien.fleche && <Icone nom="arrow_forward" />}
        </Link>
      )}
    </div>
  )
}

export function Interrupteur({
  actif,
  onChange,
  children,
}: {
  actif: boolean
  onChange: (v: boolean) => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={actif}
      className="interrupteur"
      onClick={() => onChange(!actif)}
    >
      <span className="interrupteur-piste" />
      {children}
    </button>
  )
}
